import classNames from 'classnames';
import { ClearIndicator } from 'components/shared/Tags/clear-indicator';
import { TagOption } from 'components/shared/Tags/model';
import { TagsPicker } from 'components/shared/Tags/tags-picker';
import { MenuList } from 'components/shared/underlyings/selector/result/menu-list';
import { NoOptionsMessage } from 'components/shared/underlyings/selector/result/no-option-message';
import { Option } from 'components/shared/underlyings/selector/result/option';
import { UnderlyingHeader } from 'components/shared/underlyings/underlyings-card/header';
import { UnderlyingTag } from 'components/underlying-tag';
import { useGenerateUnderlyingsGridForExcel } from 'hooks/underlyings/use-generate-underlyings-grid-for-excel';
import { useEssentialUnderlyingSearchByFormId } from 'hooks/underlyings/use-underlying-search';
import { useUnderlyingsBasket } from 'hooks/underlyings/use-underlyings-basket';
import { useUnderlyingsTags } from 'hooks/underlyings/use-underlyings-tags';

import './style.scss';
import { useCopyToExcel } from 'hooks/useCopyToExcel';
import { first } from 'lodash-es';
import { Underlying } from 'models/underlying';
import { UnderlyingCodeType } from 'models/underlying/base';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { GroupBase, ValueContainerProps } from 'react-select';

import { Result } from './result';

export type UnderlyingsCardProps = {
  underlying?: Underlying;
  setUnderlying: (underlying?: Underlying) => void;
  formId: string;
  underlyingBlock: string;
  placeholder?: string;
  defaultUnderlyingCodeType?: UnderlyingCodeType;
  tabId?: string;
  isUnderlyingResultExpanded: boolean;
  isInvalid?: boolean;
  isHighlighted?: boolean;
  onToggleUnderlyingResult: () => void;
};

const DEFAULT_UNDERLYING_CODE_TYPE = 'Bloomberg' as const;

type BadgeProps = {
  p: ValueContainerProps<TagOption<Underlying>, false, GroupBase<TagOption<Underlying>>>;
  onClear: () => void;
};

const Badge = ({ p, onClear }: BadgeProps) => {
  const value = first(p.getValue());
  if (value === undefined) {
    return null;
  }
  const { label, rawValue } = value;
  return (
    <div key={label} className="essential-single-underlying-badge d-flex align-items-center">
      <UnderlyingTag label={label} value={rawValue} onRemove={onClear} />
    </div>
  );
};

export function UnderlyingCard({
  underlying,
  setUnderlying,
  formId,
  underlyingBlock,
  placeholder,
  defaultUnderlyingCodeType,
  isUnderlyingResultExpanded,
  onToggleUnderlyingResult,
  isInvalid = false,
  isHighlighted = false,
}: UnderlyingsCardProps) {
  const { formatMessage } = useIntl();
  const { search, getOptionsFromPastedLabels } = useEssentialUnderlyingSearchByFormId(formId, underlyingBlock);
  const { onTagsChanged, tags, newValidTags, updateUnderlyings, onUnderlyingRemoved, hasTags } = useUnderlyingsTags({
    underlying,
    isMulti: false,
    onChange: setUnderlying,
    codeType: defaultUnderlyingCodeType ?? DEFAULT_UNDERLYING_CODE_TYPE,
    removeInvalidTagsOnUnderlyingsChange: true,
    underlyingsSyncMode: 'replace',
  });
  const underlyings = underlying ? [underlying] : [];
  const { selectedUnderlyings, selectedUnderlyingsIds } = useUnderlyingsBasket({ underlyings, tags });
  const { generateTableExport } = useGenerateUnderlyingsGridForExcel(underlyings);
  const onCopyAll = useCopyToExcel(generateTableExport);
  const onClear = useCallback(() => setUnderlying(undefined), [setUnderlying]);

  return (
    <div
      className={classNames(
        'card card-bordering-primary px-12px pb-2 pt-1 bg-lvl1 d-flex flex-column underlyings-card-container',
        {
          'border-bottom border-warning': isInvalid,
          'border-bottom border-info': isHighlighted,
        },
      )}
    >
      <UnderlyingHeader
        labelKey="form.Underlying"
        clearDisabled={!hasTags}
        resultShown={isUnderlyingResultExpanded}
        onClear={onClear}
        onCopyAll={onCopyAll}
        onToggleResult={onToggleUnderlyingResult}
        isCopyButtonVisible={underlyings.length > 0}
      />
      <div className="d-flex align-items-center mt-2" onBlur={updateUnderlyings}>
        <i className="icon icon-sm text-secondary">search</i>
        <TagsPicker<Underlying>
          className="h-auto"
          components={{
            MenuList,
            Option,
            NoOptionsMessage,
            ClearIndicator: ClearIndicator.Default,
            SingleValue: p => Badge({ p, onClear }),
          }}
          isMulti={false}
          classNames={{ singleValue: () => 'single-value' }}
          convertPastedLabelsToTags={inputs =>
            getOptionsFromPastedLabels(
              inputs,
              selectedUnderlyingsIds,
              defaultUnderlyingCodeType ?? DEFAULT_UNDERLYING_CODE_TYPE,
            )
          }
          filterTags={input =>
            search(
              input,
              selectedUnderlyingsIds,
              selectedUnderlyings,
              defaultUnderlyingCodeType ?? DEFAULT_UNDERLYING_CODE_TYPE,
            )
          }
          id="underlyings"
          isClearable={false}
          isMenuListNarrow
          onTagsChanged={t => onTagsChanged(t !== undefined ? [t] : [], true)}
          placeholder={placeholder ?? formatMessage({ id: 'underlying.placeholder.long' })}
          tags={tags}
        />
        <button
          data-e2e="add-underlyings-button"
          className="btn btn-sm btn-light me-1"
          disabled={newValidTags.length === 0}
        >
          <i className="icon icon-sm">done</i>
        </button>
      </div>

      {isUnderlyingResultExpanded && (
        <Result
          underlyings={underlyings}
          onUnderlyingsChanged={u => setUnderlying(first(u))}
          onUnderlyingRemoved={t => onUnderlyingRemoved(t, true)}
        />
      )}
    </div>
  );
}
